import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { GATEWAY_HTTP_URL as GATEWAY_URL } from '../config/api';
import { useAuth } from './AuthContext';

export type OrganizationRole = 'owner' | 'member';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  role: OrganizationRole;
  memberCount?: number;
  createdAt: string;
  updatedAt: string;
  allowApiTokens?: boolean;
}

export interface OrganizationMember {
  id: string;
  organizationId: string;
  userId: string;
  role: OrganizationRole;
  createdAt: string;
  user: {
    id: string;
    email: string | null;
    name: string | null;
  };
}

export interface OrganizationInvite {
  id: string;
  email: string;
  role: OrganizationRole;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  expiresAt: string;
  createdAt: string;
  organization: {
    id: string;
    name: string;
  };
  invitedBy: {
    name: string | null;
    email: string | null;
  };
}

interface OrganizationContextType {
  organizations: Organization[];
  currentOrganization: Organization | null;
  currentRole: OrganizationRole | null;
  isLoading: boolean;
  error: string | null;

  // Organization management
  fetchOrganizations: () => Promise<void>;
  selectOrganization: (orgId: string) => void;
  createOrganization: (name: string) => Promise<Organization>;
  updateOrganization: (orgId: string, data: { name?: string; allowApiTokens?: boolean }) => Promise<void>;
  deleteOrganization: (orgId: string) => Promise<void>;

  // Member management
  getMembers: (orgId: string) => Promise<OrganizationMember[]>;
  updateMemberRole: (orgId: string, userId: string, role: OrganizationRole) => Promise<void>;
  removeMember: (orgId: string, userId: string) => Promise<void>;
  leaveOrganization: (orgId: string) => Promise<void>;

  // Invitation management
  getPendingInvites: (orgId: string) => Promise<OrganizationInvite[]>;
  createInvite: (orgId: string, email: string, role: OrganizationRole) => Promise<OrganizationInvite>;
  cancelInvite: (orgId: string, inviteId: string) => Promise<void>;

  // Helper
  isOwner: boolean;
  getAuthHeaders: () => Promise<Record<string, string>>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { getToken, isAuthenticated } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to get auth headers with organization context
  const getAuthHeaders = useCallback(async () => {
    const token = await getToken();
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    if (currentOrganization) {
      headers['x-organization-id'] = currentOrganization.id;
    }
    return headers;
  }, [getToken, currentOrganization]);

  // Fetch all organizations for the current user
  const fetchOrganizations = useCallback(async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const token = await getToken();
      const response = await axios.get(`${GATEWAY_URL}/api/organizations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success && response.data.organizations) {
        const orgs = response.data.organizations;
        setOrganizations(orgs);

        // If no current organization is selected, try to restore or select first one
        if (!currentOrganization && orgs.length > 0) {
          const savedOrgId = localStorage.getItem('selectedOrganizationId');
          let orgToSelect: Organization | null = null;

          // First priority: Restore saved organization
          if (savedOrgId) {
            orgToSelect = orgs.find((o: Organization) => o.id === savedOrgId) || null;
          }

          // Second priority: Select first organization
          if (!orgToSelect) {
            orgToSelect = orgs[0];
          }

          if (orgToSelect) {
            setCurrentOrganization(orgToSelect);
            localStorage.setItem('selectedOrganizationId', orgToSelect.id);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      setError('Failed to load organizations');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getToken, currentOrganization]);

  // Select an organization
  const selectOrganization = useCallback((orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      setCurrentOrganization(org);
      localStorage.setItem('selectedOrganizationId', orgId);
      // Clear project selection when switching orgs
      localStorage.removeItem('selectedProjectId');
    }
  }, [organizations]);

  // Create a new organization
  const createOrganization = useCallback(async (name: string): Promise<Organization> => {
    const token = await getToken();
    const response = await axios.post(
      `${GATEWAY_URL}/api/organizations`,
      { name },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.data.success && response.data.organization) {
      const newOrg = { ...response.data.organization, role: 'owner' as OrganizationRole };
      setOrganizations(prev => [...prev, newOrg]);
      return newOrg;
    }

    throw new Error('Failed to create organization');
  }, [getToken]);

  // Update an organization
  const updateOrganization = useCallback(async (orgId: string, data: { name?: string; allowApiTokens?: boolean }) => {
    const headers = await getAuthHeaders();
    const response = await axios.put(
      `${GATEWAY_URL}/api/organizations/${orgId}`,
      data,
      { headers }
    );

    if (response.data.success && response.data.organization) {
      const updated = response.data.organization;
      setOrganizations(prev => prev.map(o => o.id === orgId ? { ...o, ...updated } : o));
      if (currentOrganization?.id === orgId) {
        setCurrentOrganization(prev => prev ? { ...prev, ...updated } : null);
      }
    }
  }, [getAuthHeaders, currentOrganization]);

  // Delete an organization
  const deleteOrganization = useCallback(async (orgId: string) => {
    const headers = await getAuthHeaders();
    await axios.delete(`${GATEWAY_URL}/api/organizations/${orgId}`, { headers });

    setOrganizations(prev => prev.filter(o => o.id !== orgId));

    // If the deleted org was selected, select another one
    if (currentOrganization?.id === orgId) {
      const remaining = organizations.filter(o => o.id !== orgId);
      if (remaining.length > 0) {
        setCurrentOrganization(remaining[0]);
        localStorage.setItem('selectedOrganizationId', remaining[0].id);
      } else {
        setCurrentOrganization(null);
        localStorage.removeItem('selectedOrganizationId');
      }
    }
  }, [getAuthHeaders, currentOrganization, organizations]);

  // Get members of an organization
  const getMembers = useCallback(async (orgId: string): Promise<OrganizationMember[]> => {
    const headers = await getAuthHeaders();
    const response = await axios.get(
      `${GATEWAY_URL}/api/organizations/${orgId}/members`,
      { headers }
    );

    if (response.data.success) {
      return response.data.members;
    }
    return [];
  }, [getAuthHeaders]);

  // Update member role
  const updateMemberRole = useCallback(async (orgId: string, userId: string, role: OrganizationRole) => {
    const headers = await getAuthHeaders();
    await axios.put(
      `${GATEWAY_URL}/api/organizations/${orgId}/members/${userId}`,
      { role },
      { headers }
    );
  }, [getAuthHeaders]);

  // Remove a member
  const removeMember = useCallback(async (orgId: string, userId: string) => {
    const headers = await getAuthHeaders();
    await axios.delete(
      `${GATEWAY_URL}/api/organizations/${orgId}/members/${userId}`,
      { headers }
    );
  }, [getAuthHeaders]);

  // Leave an organization
  const leaveOrganization = useCallback(async (orgId: string) => {
    const headers = await getAuthHeaders();
    await axios.delete(
      `${GATEWAY_URL}/api/organizations/${orgId}/members/me`,
      { headers }
    );

    // Remove from local state
    setOrganizations(prev => prev.filter(o => o.id !== orgId));

    // If leaving current org, select another
    if (currentOrganization?.id === orgId) {
      const remaining = organizations.filter(o => o.id !== orgId);
      if (remaining.length > 0) {
        setCurrentOrganization(remaining[0]);
        localStorage.setItem('selectedOrganizationId', remaining[0].id);
      } else {
        setCurrentOrganization(null);
        localStorage.removeItem('selectedOrganizationId');
      }
    }
  }, [getAuthHeaders, currentOrganization, organizations]);

  // Get pending invites
  const getPendingInvites = useCallback(async (orgId: string): Promise<OrganizationInvite[]> => {
    const headers = await getAuthHeaders();
    const response = await axios.get(
      `${GATEWAY_URL}/api/organizations/${orgId}/invites`,
      { headers }
    );

    if (response.data.success) {
      return response.data.invites;
    }
    return [];
  }, [getAuthHeaders]);

  // Create an invite
  const createInvite = useCallback(async (
    orgId: string,
    email: string,
    role: OrganizationRole
  ): Promise<OrganizationInvite> => {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${GATEWAY_URL}/api/organizations/${orgId}/invites`,
      { email, role },
      { headers }
    );

    if (response.data.success) {
      return response.data.invite;
    }
    throw new Error('Failed to create invite');
  }, [getAuthHeaders]);

  // Cancel an invite
  const cancelInvite = useCallback(async (orgId: string, inviteId: string) => {
    const headers = await getAuthHeaders();
    await axios.delete(
      `${GATEWAY_URL}/api/organizations/${orgId}/invites/${inviteId}`,
      { headers }
    );
  }, [getAuthHeaders]);

  // Load organizations on mount after authentication
  useEffect(() => {
    if (isAuthenticated) {
      fetchOrganizations();
    }
  }, [isAuthenticated, fetchOrganizations]);

  const value: OrganizationContextType = {
    organizations,
    currentOrganization,
    currentRole: currentOrganization?.role || null,
    isLoading,
    error,
    fetchOrganizations,
    selectOrganization,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    getMembers,
    updateMemberRole,
    removeMember,
    leaveOrganization,
    getPendingInvites,
    createInvite,
    cancelInvite,
    isOwner: currentOrganization?.role === 'owner',
    getAuthHeaders
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
